import { StudyUsersService } from '@/services/';
import { defineStore } from 'pinia';
import { reactive, ref, computed, watch } from 'vue';
import { useStudyStore } from './study';
import { UserModel, type IUserPermissions } from '@/models/user';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import { LoadingStateModel } from '@/models/loadingState';
import { useFormStore } from './forms';
import UserService from '@/services/userService';
import InviteService from '@/services/inviteService';
import StudySettingsService from '@/services/studySettingsService';
import { useAuthStore } from './auth';
import { useUserStore } from './user';
import { LocalStorageUtils } from '@/utils';


export const useUserManagementStore = defineStore('userManagement', () => {
  const studyStore = useStudyStore();
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const formStore = useFormStore();
  
  const currentUsers = ref<UserModel[]>([]);
  const pendingUsers = ref<UserModel[]>([]);
  const userInvites = ref<UserModel[]>([]);
  
  const avatar = ref<string>('');
  const avatarLoad = reactive(new LoadingStateModel({ isLoading: false }));
  const initialLoad = reactive(new LoadingStateModel({ isLoading: false }));

  const isSavingPermissions = reactive(new LoadingStateModel({isLoading: false})); // for both, active and pending users

  const tableColumns: TableColumnModel[] = [
    { id: 1, header: 'User', key: 'name', type: TableColumnType.USER, sortable: false },
    { id: 2, header: 'Permissions', type: TableColumnType.TEXT, key: 'permissionsList', sortable: true }
  ];

  const isCurrentUsersLoading = reactive(new LoadingStateModel({isLoading: false}));
  const isPendingUsersLoading = reactive(new LoadingStateModel({isLoading: false}));

  const ensureFormsLoaded = async () => {
    if (formStore.formList.length === 0) {
      await formStore.getStudyForms();
    }
  };

  const fetchCurrentUsers = async () => {
    if (studyStore.selectedStudyId) {
      try {
        isCurrentUsersLoading.startLoading();
        await ensureFormsLoaded();
        currentUsers.value = await StudyUsersService.getCurrentStudyUsers(studyStore.selectedStudyId, formStore.formList);
      } catch (error) {
        isCurrentUsersLoading.setError(error);
      } finally {
        isCurrentUsersLoading.stopLoadingAndFinish();
      }
    }
  };

  const fetchPendingUsers = async () => {
    if (studyStore.selectedStudyId) {
      try {
        isPendingUsersLoading.startLoading();
        await ensureFormsLoaded();
        pendingUsers.value = await StudyUsersService.getPendingStudyUsers(studyStore.selectedStudyId, formStore.formList);

        const allForms = formStore.formList.map(form => form.base64Id) as string[];
        userInvites.value = [UserModel.createEmpty(allForms)];
      } catch (error) {
        isPendingUsersLoading.setError(error);
      } finally {
        isPendingUsersLoading.stopLoadingAndFinish();
      }
    }
  };

  watch(() => formStore.formList, () => {
    currentUsers.value.forEach(user => {
      StudyUsersService.parseFormsPermissionsForUser(user, formStore.formList);
    });
    pendingUsers.value.forEach(user => {
      StudyUsersService.parseFormsPermissionsForUser(user, formStore.formList);
    });
  });

  const patchUserPermissions = async (userId: string, permissions: IUserPermissions) => {
    permissions.studyId = studyStore.selectedStudyId;
    if (typeof permissions.forms !== 'boolean' && (permissions.forms as string[]).length === formStore.formList.length) {
      permissions.forms = true;
    }
    if (typeof permissions.editForms !== 'boolean' && (permissions.editForms as string[]).length === formStore.formList.length) {
      permissions.editForms = true;
    }
    try {
      isSavingPermissions.startLoading();
      await UserService.patchUserPermissions(userId, permissions);
      isSavingPermissions.setSuccess(`User permissions updated!`);
      await fetchCurrentUsers();
    } catch (error) {
      isSavingPermissions.setError(error);
    } finally {
      isSavingPermissions.stopLoadingAndFinish();
    }
  };

  const isRemovingUser = reactive(new LoadingStateModel({isLoading: false}));
  const removeUserFromStudy = async (userId: string) => {
    if (studyStore.selectedStudyId) {
      try {
        isRemovingUser.startLoading();
        await StudyUsersService.removeUserFromStudy(studyStore.selectedStudyId, userId);
        isRemovingUser.setSuccess('User removed from the study!');
        await fetchCurrentUsers();
      } catch (error) {
        isRemovingUser.setError(error);
      } finally {
        isRemovingUser.stopLoadingAndFinish();
      }
    }
  };

  const isInvitingUser = reactive(new LoadingStateModel({isLoading: false}));
  const inviteUserToStudy = async (invites: UserModel[]) => {
    if (studyStore.selectedStudyId) {
      try {
        isInvitingUser.startLoading();
        
        const response = await StudyUsersService.inviteUserToStudy(studyStore.selectedStudyId, invites);
        
        const duplicates = response.duplicates;
        const hasDuplicates = duplicates && duplicates.length > 0;
        const wereNewInvitesSent = invites.length > (duplicates?.length || 0);

        if (hasDuplicates) {
          const duplicateEmails = duplicates.join(', ');
          if (wereNewInvitesSent) {
            isInvitingUser.setSuccess(`Invites sent. Already members or invited: ${duplicateEmails}`);
          } else {
            isInvitingUser.setSuccess(`Users already exist or have pending invites: ${duplicateEmails}`);
          }
        } else {
          isInvitingUser.setSuccess('Users invited to the study!');
        }

        await fetchPendingUsers();
      } catch (error) {
        isInvitingUser.setError(error);
      } finally {
        isInvitingUser.stopLoadingAndFinish();
      }
    }
  };

  const isReInvitingUser = reactive(new LoadingStateModel({isLoading: false}));
  const reInviteUserToStudy = async (invite: UserModel) => {
    try {
      isReInvitingUser.startLoading();
      await InviteService.resendUserInvite(invite.identifier);
      isReInvitingUser.setSuccess(`Invite sent!`);
    } catch (error) {
      isReInvitingUser.setError(error);
    } finally {
      isReInvitingUser.stopLoadingAndFinish();
    }
  };
  
  const isDeletingInvitedUser = reactive(new LoadingStateModel({isLoading: false}));
  const deleteInvitedUserFromStudy = async (invite: UserModel) => {
    try {
      isDeletingInvitedUser.startLoading();
      await InviteService.deleteUserInvite(invite.identifier);
      isDeletingInvitedUser.setSuccess(`Invite deleted!`);
      await fetchPendingUsers();
    } catch (error) {
      isDeletingInvitedUser.setError(error);
    } finally {
      isDeletingInvitedUser.stopLoadingAndFinish();
    }
  };

  const isAvatarLoading = computed(() => avatarLoad.isLoading);

  const getUserAvatar = async () => {
    avatarLoad.startLoading();
    try {
      const response = await StudySettingsService.getUserAvatar(authStore.userData?.avatar ?? '') as string;
      avatar.value = response;
    } catch (error) {
      avatarLoad.setError(error);
    } finally {
      avatarLoad.stopLoadingAndFinish();
    }
  };

  const patchUserDetails = async () => {
    initialLoad.startLoading();
    try {
      await UserService.patchUserDetails({ name: authStore.userData?.name ?? '' }, userStore.user?.identifier ?? '');
      LocalStorageUtils.setUserName(authStore.userData?.name ?? '');
      initialLoad.setSuccess('User details updated!');
    } catch (error) {
      initialLoad.setError('Something went wrong');
    } finally {
      initialLoad.stopLoadingAndFinish();
    }
  };

  const patchUserAvatar = async () => {
    avatarLoad.startLoading();
    try {
      await UserService.patchUserDetails({ avatar: avatar.value }, userStore.user?.identifier ?? '');
      await userStore.getUser(userStore.user?.identifier ?? '');
      const newAvatar = userStore.user?.avatar;
      if (newAvatar) {
        authStore.userData!.avatar = newAvatar;
        LocalStorageUtils.setUserAvatar(newAvatar);
      }
      avatarLoad.setSuccess('User avatar updated!');
    } catch (error) {
      initialLoad.setError('Something went wrong');
    } finally {
      avatarLoad.stopLoadingAndFinish();
    }
  };

  const patchInvitePermissions = async (invite: UserModel, permissions: IUserPermissions) => {
    if (studyStore.selectedStudyId) {
      permissions.studyId = studyStore.selectedStudyId;
      if (typeof permissions.forms !== 'boolean' && (permissions.forms as string[]).length === formStore.formList.length) {
        permissions.forms = true;
      }
      if (typeof permissions.editForms !== 'boolean' && (permissions.editForms as string[]).length === formStore.formList.length) {
        permissions.editForms = true;
      }
      try {
        isSavingPermissions.startLoading();
        await StudyUsersService.patchInvitePermissions(studyStore.selectedStudyId, invite.identifier, permissions);
        isSavingPermissions.setSuccess('Invite permissions updated!');
        await fetchPendingUsers();
      } catch (error) {
        isSavingPermissions.setError(error);
      } finally {
        isSavingPermissions.stopLoadingAndFinish();
      }
    }
  };

  return {
    fetchCurrentUsers,
    fetchPendingUsers,
    currentUsers,
    pendingUsers,
    isCurrentUsersLoading,
    isPendingUsersLoading,
    avatar,
    isAvatarLoading,
    avatarLoad,
    initialLoad,
    patchUserPermissions,
    removeUserFromStudy,
    inviteUserToStudy,
    reInviteUserToStudy,
    deleteInvitedUserFromStudy,
    userInvites,
    tableColumns,
    isSavingPermissions,
    isRemovingUser,
    isInvitingUser,
    isReInvitingUser,
    isDeletingInvitedUser,
    patchUserDetails,
    getUserAvatar,
    patchUserAvatar,
    patchInvitePermissions
  };
});
